import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import {
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  updateProfile,
} from "firebase/auth";
import { doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { auth, db, provider } from "../firebase";
import { sanitizeForFirestore } from "../lib/firebaseSafe";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const off = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (!currentUser) {
        setProfile(null);
        setLoading(false);
        return;
      }

      const profileRef = doc(db, "users", currentUser.uid);
      const snap = await getDoc(profileRef);
      const baseProfile = {
        uid: currentUser.uid,
        email: currentUser.email || "",
        displayName: currentUser.displayName || "",
        photoURL: currentUser.photoURL || "",
        updatedAt: serverTimestamp(),
      };

      if (!snap.exists()) {
        await setDoc(profileRef, sanitizeForFirestore({
          ...baseProfile,
          createdAt: serverTimestamp(),
        }));
        setProfile(baseProfile);
      } else {
        await setDoc(profileRef, sanitizeForFirestore(baseProfile), { merge: true });
        setProfile({ ...snap.data(), ...baseProfile });
      }
      setLoading(false);
    });

    return () => off();
  }, []);

  const register = async ({ email, password, displayName }) => {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    if (displayName) {
      await updateProfile(result.user, { displayName });
    }
    await setDoc(
      doc(db, "users", result.user.uid),
      sanitizeForFirestore({
        uid: result.user.uid,
        email,
        displayName: displayName || "",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      }),
      { merge: true }
    );
    return result.user;
  };

  const login = ({ email, password }) => signInWithEmailAndPassword(auth, email, password);
  const loginWithGoogle = () => signInWithPopup(auth, provider);
  const logout = () => signOut(auth);

  const value = useMemo(
    () => ({
      user,
      profile,
      loading,
      register,
      login,
      loginWithGoogle,
      logout,
    }),
    [user, profile, loading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
